/**
 * Modal.tsx, Drawer.tsx — Overlay components.
 *
 * Both use a backdrop + content pattern.
 * Modal is centered on screen; Drawer slides in from the right.
 *
 * Keyboard: Escape closes both. Click on backdrop closes both (unless dismissible=false).
 */

import { ReactNode, useEffect } from 'react';
import { theme } from '../../theme';
import { Icon } from './Icons';
import { IconButton } from './Button';

/* ═══════════════ BACKDROP ═══════════════ */

function Backdrop({ onClick }: { onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(3, 6, 14, 0.7)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: theme.z.modal,
        animation: 'meridian-fade 160ms ease',
      }}
    />
  );
}

/* ═══════════════ MODAL ═══════════════ */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  dismissible?: boolean;
}

export function Modal({
  open, onClose, title, description, children, footer,
  size = 'md', dismissible = true,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  const maxWidth = {
    sm: 400,
    md: 520,
    lg: 720,
    xl: 960,
  }[size];

  return (
    <>
      <style>{`
        @keyframes meridian-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes meridian-modal-in {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 8px)); }
          to   { opacity: 1; transform: translate(-50%, -50%); }
        }
      `}</style>
      <Backdrop onClick={dismissible ? onClose : undefined} />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `calc(100% - ${theme.space[8]})`,
          maxWidth,
          maxHeight: `calc(100vh - ${theme.space[10]})`,
          background: theme.surface.elevated,
          border: `1px solid ${theme.borderColor.default}`,
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadow.modal,
          display: 'flex',
          flexDirection: 'column',
          zIndex: theme.z.modal + 1,
          animation: 'meridian-modal-in 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {(title || dismissible) && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: theme.space[4],
            padding: `${theme.space[5]} ${theme.space[6]}`,
            borderBottom: `1px solid ${theme.borderColor.subtle}`,
          }}>
            <div>
              {title && (
                <h2 style={{
                  fontSize: theme.fontSize.lg,
                  fontWeight: theme.fontWeight.semibold,
                  color: theme.text.primary,
                  margin: 0,
                  lineHeight: 1.3,
                }}>
                  {title}
                </h2>
              )}
              {description && (
                <p style={{
                  fontSize: theme.fontSize.sm,
                  color: theme.text.tertiary,
                  margin: 0,
                  marginTop: 4,
                }}>
                  {description}
                </p>
              )}
            </div>
            {dismissible && (
              <IconButton
                icon={<Icon.X size={16} />}
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label="Close dialog"
              />
            )}
          </div>
        )}

        <div style={{
          padding: theme.space[6],
          overflowY: 'auto',
          flex: 1,
          fontSize: theme.fontSize.base,
          color: theme.text.primary,
          fontFamily: theme.font.ui,
        }}>
          {children}
        </div>

        {footer && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.space[2],
            padding: `${theme.space[4]} ${theme.space[6]}`,
            borderTop: `1px solid ${theme.borderColor.subtle}`,
            background: theme.surface.panel,
            borderBottomLeftRadius: theme.radius.lg,
            borderBottomRightRadius: theme.radius.lg,
          }}>
            {footer}
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════════════ DRAWER ═══════════════ */

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: number;
  side?: 'right' | 'left';
}

export function Drawer({
  open, onClose, title, children, width = 420, side = 'right',
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <>
      <style>{`@keyframes meridian-fade { from { opacity: 0; } to { opacity: 1; } }`}</style>
      {open && <Backdrop onClick={onClose} />}
      <div
        style={{
          position: 'fixed',
          top: 0, bottom: 0,
          [side]: 0,
          width,
          maxWidth: '100vw',
          background: theme.surface.panel,
          borderLeft: side === 'right' ? `1px solid ${theme.borderColor.default}` : undefined,
          borderRight: side === 'left' ? `1px solid ${theme.borderColor.default}` : undefined,
          zIndex: theme.z.drawer,
          transform: open
            ? 'translateX(0)'
            : side === 'right'
            ? `translateX(${width}px)`
            : `translateX(-${width}px)`,
          transition: `transform ${theme.motion.slow}`,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: open ? theme.shadow.modal : 'none',
        } as React.CSSProperties}
      >
        {title && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${theme.space[4]} ${theme.space[5]}`,
            borderBottom: `1px solid ${theme.borderColor.subtle}`,
            flexShrink: 0,
          }}>
            <div style={{
              fontSize: theme.fontSize.md,
              fontWeight: theme.fontWeight.semibold,
              color: theme.text.primary,
            }}>
              {title}
            </div>
            <IconButton
              icon={<Icon.X size={16} />}
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close drawer"
            />
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </>
  );
}
